import { createSlice } from '@reduxjs/toolkit'

// Фабрика для создания конфигурации прав доступа
const createAccessConfig = (permissions) => ({
  actions: {
    orders: {
      ...permissions,
      order: { ...permissions },
    },
    contacts: {
      ...permissions,
      contact: { ...permissions },
    },
    messages: {
      ...permissions,
    },
    logistic: {
      ...permissions,
      driver: { ...permissions },
      route: { ...permissions },
      vehicle: { ...permissions },
    },
    catalog: {
      ...permissions,
      product: { ...permissions },
      productManager: { ...permissions },
      lowProfit: { ...permissions },
      SPOManagment: { ...permissions },
      personalRates: { ...permissions },
    },
    billings: {
      invoicing: { ...permissions },
      debtors: { ...permissions },
    },
    operations: {
      shopping: { ...permissions },
      buyer: { ...permissions },
      orderHistory: {
        ...permissions,
      },
      stock: { ...permissions },
      picking: { ...permissions },
    },
    suppliers: {
      juice: { ...permissions },
      cuartagamma: { ...permissions },
      coctail: { ...permissions },
      oriental: { ...permissions },
    },
    integration: {
      ...permissions,
    },
    mbApp: {
      ...permissions,
    },
  },
})

// Определение прав для каждой роли
const adminAccess = createAccessConfig({
  canDelete: true,
  canEdit: true,
  canExport: true,
  canCopy: true,
  canView: true,
  canPrint: true,
  canCreate: true,
})

const viewerAccess = createAccessConfig({
  canDelete: false,
  canEdit: false,
  canExport: false,
  canCopy: false,
  canView: false,
  canPrint: false,
  canCreate: false,
})

const userAccess = createAccessConfig({
  canDelete: false,
  canEdit: false,
  canExport: true,
  canCopy: false,
  canView: true,
  canPrint: true,
  canCreate: false,
})

const driverAccess = createAccessConfig({
  canDelete: false,
  canEdit: false,
  canExport: false,
  canCopy: false,
  canView: true,
  canPrint: true,
  canCreate: false,
})

const pickerAccess = createAccessConfig({
  canDelete: false,
  canEdit: false,
  canExport: false,
  canCopy: false,
  canView: true,
  canPrint: true,
  canCreate: false,
})

// Функция для получения прав по роли
const getAccessByRole = (role) => {
  switch (role) {
    case 'admin':
      return adminAccess
    case 'user':
      return userAccess
    case 'driver':
      return driverAccess
    case 'picker':
      return pickerAccess
    case 'viewer':
    default:
      return viewerAccess
  }
}

const initialState = {
  userRole: null,
  permissions: null,
  isLoading: false,
  error: null,
}

export const userPermissionsSlice = createSlice({
  name: 'userPermissions',
  initialState,
  reducers: {
    setUserPermissions: (state, action) => {
      const { role, permissions } = action.payload
      state.userRole = role
      state.permissions =
        permissions || getAccessByRole(role)
      state.isLoading = false
      state.error = null
    },
    clearUserPermissions: (state) => {
      state.userRole = null
      state.permissions = null
      state.isLoading = false
      state.error = null
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.isLoading = false
    },
  },
})

export const {
  setUserPermissions,
  clearUserPermissions,
  setLoading,
  setError,
} = userPermissionsSlice.actions

// Селекторы для удобного доступа к правам
export const selectUserRole = (state) =>
  state.userPermissions.userRole
export const selectUserPermissions = (state) =>
  state.userPermissions.permissions
export const selectIsLoading = (state) =>
  state.userPermissions.isLoading
export const selectError = (state) =>
  state.userPermissions.error

// Селектор для проверки конкретного права
export const selectHasPermission =
  (module, action) => (state) => {
    const permissions = state.userPermissions.permissions
    if (!permissions || !permissions.actions) return false

    const modulePermissions = permissions.actions[module]
    if (!modulePermissions) return false

    return modulePermissions[action] === true
  }

// Селектор для проверки прав на подмодуль
export const selectHasSubModulePermission =
  (module, subModule, action) => (state) => {
    const permissions = state.userPermissions.permissions
    if (!permissions || !permissions.actions) return false

    const modulePermissions = permissions.actions[module]
    if (!modulePermissions || !modulePermissions[subModule])
      return false

    return modulePermissions[subModule][action] === true
  }

export default userPermissionsSlice.reducer
